import React, { useState } from "react";
import { GripVertical, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Button } from "../../../Components/ui/shadcn/button";
import { cn } from "../../../lib/utils";
import type { DisplayMenu } from "@/api/type";

interface DraggableDisplayMenuListProps {
    displayMenus: DisplayMenu[];
    onReorder: (newOrder: DisplayMenu[]) => void;
    onEdit: (displayMenu: DisplayMenu) => void;
    onDelete: (id: number) => void;
    onToggleActive: (id: number) => void;
}

const DraggableDisplayMenuList: React.FC<DraggableDisplayMenuListProps> = ({
    displayMenus,
    onReorder,
    onEdit,
    onDelete,
    onToggleActive,
}) => {
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
        (e.target as HTMLElement).classList.add("opacity-50");
    };

    const handleDragEnd = (e: React.DragEvent) => {
        (e.target as HTMLElement).classList.remove("opacity-50");
        setDraggedItemIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) {
            return;
        }

        const newDisplayMenus = [...displayMenus];
        const draggedItem = newDisplayMenus[draggedItemIndex];
        newDisplayMenus.splice(draggedItemIndex, 1);
        newDisplayMenus.splice(index, 0, draggedItem);

        const updatedDisplayMenus = newDisplayMenus.map((item, orderIndex) => ({
            ...item,
            display_order: orderIndex,
        }));

        setDraggedItemIndex(index);
        onReorder(updatedDisplayMenus);
    };

    return (
        <div className="space-y-2">
            {displayMenus.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-10 text-center text-sm text-gray-400">
                    등록된 메뉴 안내가 없습니다. "+ 추가" 버튼을 눌러 등록해주세요.
                </div>
            ) : (
                displayMenus.map((displayMenu, index) => (
                    <div
                        key={displayMenu.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        className={cn(
                            "flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-gray-300",
                            draggedItemIndex === index && "bg-gray-50 ring-2 ring-black"
                        )}
                    >
                        <div className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing">
                            <GripVertical size={20} />
                        </div>

                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-100 bg-gray-100 text-[10px] text-gray-300">
                            {displayMenu.image ? (
                                <img
                                    src={displayMenu.image}
                                    alt={displayMenu.title}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                "No Img"
                            )}
                        </div>

                        <div className="min-w-0 flex-grow">
                            <div className="truncate text-sm font-semibold text-gray-900">
                                {displayMenu.title}
                            </div>
                            <div className="truncate text-[10px] text-gray-500">
                                {displayMenu.description || "설명 없음"}
                            </div>
                            <div className="text-[10px] text-gray-500">
                                순서: {displayMenu.display_order + 1}
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-gray-600"
                                onClick={() => onToggleActive(displayMenu.id)}
                            >
                                {displayMenu.is_active ? (
                                    <Eye size={18} />
                                ) : (
                                    <EyeOff size={18} className="text-red-400" />
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-blue-600"
                                onClick={() => onEdit(displayMenu)}
                            >
                                <Edit size={18} />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-red-600"
                                onClick={() => onDelete(displayMenu.id)}
                            >
                                <Trash2 size={18} />
                            </Button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default DraggableDisplayMenuList;
